import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md" }
const _hoisted_2 = { class: "calendar-wrap trezo-card-content relative" }
const _hoisted_3 = { class: "mb-4 flex justify-center" }
const _hoisted_4 = {
  class: "inline-flex rounded-md shadow-sm",
  role: "group"
}
const _hoisted_5 = {
  key: 0,
  class: "flex justify-center my-5"
}
const _hoisted_6 = {
  key: 1,
  id: "calendar"
}
const _hoisted_7 = { class: "flex justify-between items-center mt-5" }
const _hoisted_8 = { class: "inline-block relative ltr:pl-[22px] rtl:pr-[22px]" }
const _hoisted_9 = { class: "inline-block relative ltr:pl-[22px] rtl:pr-[22px]" }

import { onMounted, ref, onUnmounted } from "vue";
import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { supabase } from "@/lib/supabaseClient";
import { createToast } from "@/utils/toast";
import stateStore from "@/utils/store";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'CalendarContent',
  emits: ['event-click', 'edit-working-hours'],
  setup(__props, { emit: __emit }) {

const emit = __emit;

const stateStoreInstance = stateStore;
let calendarInstance: Calendar;
const currentView = ref('dayGridMonth');
const loading = ref(true);
const events = ref<any[]>([]);
const businessHours = ref<any[]>([]);
const userId = ref<string | null>(null);

// i18n setup
const { locale } = useI18n();

// Translation files
const translationFiles = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations
};

// Direct translation function that bypasses vue-i18n
const directT = (key: string): string => {
  const parts = key.split('.');
  let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
  
  for (const part of parts) {
    if (!result || !(part in result)) {
      console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
      // Fallback to English if translation not found
      let enResult: TranslationObject = translationFiles.en;
      for (const fallbackPart of parts) {
        if (!enResult || !(fallbackPart in enResult)) return key;
        enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
      }
      return typeof enResult === 'string' ? enResult : key;
    }
    result = result[part as keyof typeof result] as TranslationObject;
  }
  return typeof result === 'string' ? result : key;
};

// Style overrides for calendar
const calendarStyles = document.createElement('style');
calendarStyles.innerHTML = `
  .fc .fc-daygrid-day.fc-day-today {
    background-color: rgba(var(--primary-500-rgb), 0.15) !important;
  }
  .fc .fc-timegrid-col.fc-day-today {
    background-color: rgba(var(--primary-500-rgb), 0.15) !important;
  }
`;
document.head.appendChild(calendarStyles);

function changeView(viewName: string) {
  console.log('Changing view to:', viewName);
  currentView.value = viewName;
  if (calendarInstance) {
    calendarInstance.changeView(viewName);
    // After view change, make sure events are properly displayed
    calendarInstance.removeAllEvents();
    calendarInstance.addEventSource(events.value);
    console.log('Current events in calendar after refresh:', calendarInstance.getEvents());
  }
}

// Function to emit event for editing working hours
function editWorkingHours() {
  emit('edit-working-hours');
}

// Function to add new event using state store
function addNewEvent() {
  stateStoreInstance.activeBtn();
}

// Fetch employee availability from database
async function fetchEmployeeAvailability() {
  try {
    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    userId.value = user.id;
    
    const { data, error } = await supabase
      .from('employee_availability')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .single();
    
    if (error) {
      console.error('Error fetching employee availability:', error);
      return;
    }
    
    if (!data) {
      console.log('No availability data found for the user');
      return;
    }
    
    console.log('Employee availability data:', data);
    
    // Format business hours for FullCalendar
    const formattedBusinessHours = [];
    
    // Days of week in FullCalendar are 0=Sunday to 6=Saturday
    const daysMap = [
      { key: 'sunday_available', dayOfWeek: 0, start: 'sunday_start_time', end: 'sunday_end_time' },
      { key: 'monday_available', dayOfWeek: 1, start: 'monday_start_time', end: 'monday_end_time' },
      { key: 'tuesday_available', dayOfWeek: 2, start: 'tuesday_start_time', end: 'tuesday_end_time' },
      { key: 'wednesday_available', dayOfWeek: 3, start: 'wednesday_start_time', end: 'wednesday_end_time' },
      { key: 'thursday_available', dayOfWeek: 4, start: 'thursday_start_time', end: 'thursday_end_time' },
      { key: 'friday_available', dayOfWeek: 5, start: 'friday_start_time', end: 'friday_end_time' },
      { key: 'saturday_available', dayOfWeek: 6, start: 'saturday_start_time', end: 'saturday_end_time' }
    ];
    
    for (const day of daysMap) {
      if (data[day.key]) {
        formattedBusinessHours.push({
          daysOfWeek: [day.dayOfWeek],
          startTime: data[day.start],
          endTime: data[day.end]
        });
      }
    }
    
    businessHours.value = formattedBusinessHours;
    console.log('Formatted business hours:', businessHours.value);
    
    // If calendar already exists, update business hours
    if (calendarInstance) {
      calendarInstance.setOption('businessHours', businessHours.value);
      // Also set non-business hours to be visually different
      calendarInstance.setOption('slotLabelFormat', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      calendarInstance.render();
    }
    
  } catch (err: any) {
    console.error('Error setting business hours:', err);
  }
}

// Fetch events from database
async function fetchEvents() {
  try {
    loading.value = true;
    
    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .order('start_time', { ascending: true });
    
    if (error) throw error;
    
    console.log('Raw events from database:', data);
    
    // Convert data to FullCalendar format with proper time handling
    events.value = data.map((event: any) => {
      console.log('Raw event from database:', event);
      
      // Extract the time part from the ISO string for consistent display
      const extractTime = (isoString: string) => {
        if (!isoString) return '';
        const timeMatch = isoString.match(/T(\d{2}:\d{2})/);
        return timeMatch ? timeMatch[1] : '';
      };
      
      // Add the display time to the title for consistent preview
      const timeStr = extractTime(event.start_time);
      const displayTitle = timeStr ? `${timeStr} - ${event.title}` : event.title;
      
      // For correct positioning in time grid views, we need to transform the time
      // while preserving the original time in local format for display
      const processTimeForGrid = (isoString: string) => {
        if (!isoString) return '';
        
        // Parse the ISO string to extract date and time components
        const match = isoString.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
        if (!match) return isoString;
        
        const [_, datePart, timePart] = match;
        const [hours, minutes] = timePart.split(':').map(Number);
        
        // Create a date object using local time interpretation
        // This ensures proper positioning in the time grid
        const dateObj = new Date(`${datePart}T00:00:00`);
        dateObj.setHours(hours, minutes, 0, 0);
        
        return dateObj;
      };
      
      const formattedEvent = {
        id: event.id,
        title: displayTitle, // Include time in title for consistent display
        // Process times for correct grid positioning
        start: processTimeForGrid(event.start_time),
        end: processTimeForGrid(event.end_time),
        displayEventTime: false, // We're including the time in the title directly
        allDay: false,
        extendedProps: {
          eventType: event.event_type,
          customerName: event.customer_name,
          phone: event.phone,
          location: event.location,
          description: event.description,
          userId: event.user_id,
          originalTime: timeStr,
          originalTitle: event.title,
          // Store original ISO strings for reference
          originalStart: event.start_time,
          originalEnd: event.end_time
        }
      };
      console.log('Formatted event for calendar:', formattedEvent);
      return formattedEvent;
    });
    
    console.log('All formatted events:', events.value);
    
    // If calendar already exists, update events
    if (calendarInstance) {
      calendarInstance.removeAllEvents();
      calendarInstance.addEventSource(events.value);
    }
    
  } catch (err: any) {
    console.error('Error loading events:', err);
    createToast('Error loading events: ' + (err.message || err), 'error');
  } finally {
    loading.value = false;
  }
}

// Listen for add/update events
function setupEventListeners() {
  window.addEventListener('calendar-event-added', () => {
    fetchEvents();
  });
  window.addEventListener('calendar-event-updated', () => {
    fetchEvents();
  });
  window.addEventListener('calendar-event-deleted', () => {
    fetchEvents();
  });
  // Add listener for workforce availability updates
  window.addEventListener('workforce-updated', () => {
    fetchEmployeeAvailability();
  });
}

// Clean up event listeners
function cleanupEventListeners() {
  window.removeEventListener('calendar-event-added', () => {
    fetchEvents();
  });
  window.removeEventListener('calendar-event-updated', () => {
    fetchEvents();
  });
  window.removeEventListener('calendar-event-deleted', () => {
    fetchEvents();
  });
  // Remove listener for workforce availability updates
  window.removeEventListener('workforce-updated', () => {
    fetchEmployeeAvailability();
  });
}

// Initialize calendar with specific settings
function initCalendar() {
  const calendarEl = document.getElementById("calendar");
  if (!calendarEl) return;
  
  calendarInstance = new Calendar(calendarEl, {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    headerToolbar: {
      left: "title",
      right: "today prev,next",
    },
    initialView: currentView.value,
    navLinks: true,
    editable: true,
    dayMaxEvents: true,
    slotMinTime: '00:00:00',
    slotMaxTime: '24:00:00',
    forceEventDuration: true, // Enforce end time for events
    defaultTimedEventDuration: '01:00:00', // Default 1 hour if no end time
    displayEventTime: false, // Don't use the calendar's time formatting
    nowIndicator: true, // Shows a line for current time in timeGrid views
    now: new Date(), // Explicitly set today's date
    timeZone: 'local', // Use local timezone for proper day indication
    businessHours: businessHours.value,
    // Apply non-business hours styling
    nonBusinessHours: {
      display: 'background',
      color: '#d3d3d3'
    },
    views: {
      timeGridDay: {
        dayHeaderFormat: { weekday: 'long', month: 'numeric', day: 'numeric', omitCommas: true },
        slotLabelFormat: {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }
      },
      timeGridWeek: {
        dayHeaderFormat: { weekday: 'short', month: 'numeric', day: 'numeric', omitCommas: true },
        slotLabelFormat: {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }
      }
    },
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    },
    events: events.value,
    eventClick: function(info) {
      // Instead of showing alert, emit event to parent
      emit('event-click', info.event);
    },
    dateClick: function(info) {
      // When clicking on a date, switch to timeGridDay view for that date
      calendarInstance.changeView('timeGridDay', info.dateStr);
    }
  });
  
  calendarInstance.render();
}

onMounted(async () => {
  // Load employee availability
  await fetchEmployeeAvailability();
  
  // Load events from database
  await fetchEvents();
  
  // Set up event listeners
  setupEventListeners();
  
  // Initialize calendar
  initCalendar();
});

onUnmounted(() => {
  // Clean up event listeners
  cleanupEventListeners();
  
  // Clean up calendar
  if (calendarInstance) {
    calendarInstance.destroy();
  }
});

// Listen for realtime changes from Supabase
supabase
  .channel('public:calendar_events')
  .on('postgres_changes', { event: '*', schema: 'public', table: 'calendar_events' }, () => {
    fetchEvents();
  })
  .subscribe();

// Listen for realtime changes to employee availability
supabase
  .channel('public:employee_availability')
  .on('postgres_changes', { event: '*', schema: 'public', table: 'employee_availability' }, (payload) => {
    // Only update if the changes affect our user
    if (userId.value && payload.new && payload.new.user_id === userId.value) {
      console.log('Employee availability changed, refreshing calendar');
      fetchEmployeeAvailability();
    }
  })
  .subscribe();

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("button", {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (changeView('timeGridDay'))),
            type: "button",
            class: "px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-l-lg hover:bg-gray-100 hover:text-primary-500 dark:bg-[#15203c] dark:text-white dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
          }, _toDisplayString(directT('calendar.day')), 1),
          _createElementVNode("button", {
            onClick: _cache[1] || (_cache[1] = ($event: any) => (changeView('timeGridWeek'))),
            type: "button",
            class: "px-4 py-2 text-sm font-medium text-gray-900 bg-white border-t border-b border-gray-200 hover:bg-gray-100 hover:text-primary-500 dark:bg-[#15203c] dark:text-white dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
          }, _toDisplayString(directT('calendar.week')), 1),
          _createElementVNode("button", {
            onClick: _cache[2] || (_cache[2] = ($event: any) => (changeView('dayGridMonth'))),
            type: "button",
            class: "px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-r-md hover:bg-gray-100 hover:text-primary-500 dark:bg-[#15203c] dark:text-white dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
          }, _toDisplayString(directT('calendar.month')), 1)
        ])
      ]),
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[3] || (_cache[3] = [
            _createElementVNode("div", { class: "animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" }, null, -1)
          ])))
        : (_openBlock(), _createElementBlock("div", _hoisted_6)),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("button", {
          type: "button",
          class: "inline-block transition-all rounded-md font-medium px-[13px] py-[6px] text-primary-500 border border-primary-500 hover:bg-primary-500 hover:text-white",
          onClick: editWorkingHours
        }, [
          _createElementVNode("span", _hoisted_8, [
            _cache[4] || (_cache[4] = _createElementVNode("i", { class: "material-symbols-outlined !text-[22px] absolute ltr:-left-[4px] rtl:-right-[4px] top-1/2 -translate-y-1/2" }, " schedule ", -1)),
            _createTextVNode(" " + _toDisplayString(directT('calendar.editWorkingHours')), 1)
          ])
        ]),
        _createElementVNode("button", {
          type: "button",
          class: "inline-block transition-all rounded-md font-medium px-[13px] py-[6px] text-primary-500 border border-primary-500 hover:bg-primary-500 hover:text-white",
          onClick: addNewEvent
        }, [
          _createElementVNode("span", _hoisted_9, [
            _cache[5] || (_cache[5] = _createElementVNode("i", { class: "material-symbols-outlined !text-[22px] absolute ltr:-left-[4px] rtl:-right-[4px] top-1/2 -translate-y-1/2" }, " add ", -1)),
            _createTextVNode(" " + _toDisplayString(directT('calendar.addNewEvent')), 1)
          ])
        ])
      ])
    ])
  ]))
}
}

})